import { InputLabel, Typography } from "@mui/material";
import EAIcon from "components/common/EAIcon";
import i18next from "i18next";
import { useContext, useEffect, useState } from "react";
import { Context } from "states/Store";
import useStyles from 'style/js-style/components/thirdparties/securityRating/SecurityRatingStyle';

import AwesomeDebouncePromise from "awesome-debounce-promise";
import clsx from 'clsx';
import EAButton from "components/common/EAButton";
import EATooltip from "components/common/EATooltip";
import EAAutoCompleteInput from "components/inputs/EAAutoCompleteInput";
import PrefixedTextField from "components/inputs/PrefixedInput";
import { isEmpty } from "lodash";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { Trans } from "react-i18next";
import { PrivilegeEnum, RoleBasedAccessControlContext } from "services/common/RolesUtils";
import SecurityRatingCompanyScopeModal from "./SecurityRatingCompanyScopeModal";
import { SecurityRatingContext } from "services/common/SecurityRatingUtils";
import EAColorIcon from "components/common/EAColorIcon";

const getFormDefaultValues = (config) => {
  return {
    "companyScope": config?.companyScope,
    "userCompanyLabel": config?.userCompanyLabel || ""
  };
};


const mapCompanyScopeToOptions = (companyScope) => {
  return companyScope?.map(t => {
    if (typeof t === 'object') {
      return t;
    }
    return { value: t, label: t };
  }) || []
}

const SecurityRatingForm = ({ onFormUpdate = () => { }, onSubmit = () => { }, goBack, isLoading = false }) => {
  const { classes } = useStyles();
  const [state,] = useContext(Context);
  const [options, setOptions] = useState([]);
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);
  const [isCompanyScopeModalOpen, setCompanyScopeModalOpen] = useState(false);


  const { config } = useContext(SecurityRatingContext);


  const isValidUrl = (urlString) => {
    if (isEmpty(urlString)) {
      return true;
    }
    const urlPattern = new RegExp(
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // validate domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // validate OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // validate port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // validate query string
      '(\\#[-a-z\\d_]*)?$', 'i'); // validate fragment locator
    const isValid = !!urlPattern.test(urlString);
    return isValid;
  }

  const onChange = (target) => {
    setOptions(mapCompanyScopeToOptions(target));
  }

  const formMethods = useForm({
    mode: "onChange",
    delayError: 500,
    defaultValues: getFormDefaultValues(config)
  });

  const {
    control,
    getValues,
    setValue,
    setError,
    handleSubmit,
    formState: { errors }
  } = formMethods;

  useEffect(() => {
    if (!isEmpty(config)) {
      setValue("companyScope", config.companyScope || [])
      setOptions(mapCompanyScopeToOptions(config.companyScope));
      setValue("userCompanyLabel", config.userCompanyLabel)
    }
  }, [config, setValue])

  const validateScope = (companyScope) => {
    return companyScope?.every(value => isValidUrl(value)) ? null : "Un ou plusieurs noms de domaines sont faux";
  }

  const AssetsTooltipTitle = () => {
    return <>
      <Typography fontWeight="bold">
        {i18next.t("securityRating.form.assetsTooltip.title")}
      </Typography>
    </>
  }

  const AssetsTooltipSubTitle = () => {
    return (
      <>
        {i18next.t("securityRating.form.assetsTooltip.bloc")}
        <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
          {[1, 2, 3, 4].map(nbr => (
            <li key={nbr}>{i18next.t(`securityRating.form.assetsTooltip.option${nbr}`)}</li>
          ))}
        </ul>
      </>
    )
  }

  const submitEvaluation = async (data) => {
    if (isEmpty(data?.companyScope)) {
      setError('companyScope', {
        message: i18next.t("infos.required"),
      });
    } else if (isEmpty(errors)) {
      onSubmit(data);
    }
  }

  const validateUserCompanyLabel = AwesomeDebouncePromise(async (value) => {
    return value && value?.includes("\\") ? i18next.t("securityRating.form.userCompanyLabelError") : null;
  }, 500);

  return (
    <div className={classes.formContainer}>
      <FormProvider {...formMethods}>
        {goBack && (
          <div className={classes.goBack} onClick={goBack}>
            <EAIcon icon="/arrow/arrow-back-navigation" />
            {i18next.t("back")}
          </div>
        )}
        <form
          onBlur={() => onFormUpdate(getValues())}
          onSubmit={handleSubmit(submitEvaluation)}
          className={classes.formWrapper}
        >
          <div className={classes.formInputs}>
            <div className={classes.companyScopeLabel}>
              <InputLabel className={classes.inputLabel}>
                {i18next.t("securityRating.form.companyScopeLabel")}*
                <span className={clsx(classes.moreDetailsBtn, classes.companyScopeLabelBtn)} onClick={() => setCompanyScopeModalOpen(true)}>
                  {i18next.t("moreDetails")}<EAIcon icon="call_made" />
                </span>
              </InputLabel>
              <EAAutoCompleteInput
                fieldName="companyScope"
                options={options}
                placeholder={i18next.t("securityRating.form.companyScopePlaceholder")}
                variant="outlined"
                multiple
                limitTags={6}
                freeSolo
                maxValues={10000}
                disabled={!hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_SECURITY_RATING_SOURCE)}
                clearPlaceHolderOnInput={true}
                disableClearable={true}
                onChange={onChange}
                validate={validateScope}
              />
            </div>
            <div className={classes.userCompanyLabel}>
              <InputLabel className={classes.inputLabel}>
                {i18next.t("securityRating.form.userCompanyLabel")}
                <span className={classes.userCompanyLabelFacultative}>({i18next.t("factultative")})</span>
              </InputLabel>
              <div >
                <Controller
                  control={control}
                  name="userCompanyLabel"
                  rules={{
                    validate: (value) => validateUserCompanyLabel(value),
                  }}
                  render={({ field }) => (
                    <PrefixedTextField
                      id="userCompanyLabel"
                      sx={{ m: 1, width: '25ch' }}
                      fullWidth
                      placeholder={i18next.t("securityRating.form.userCompanyLabelPlaceholder")}
                      variant="outlined"
                      error={!!errors.userCompanyLabel}
                      helperText={!!errors.userCompanyLabel ? errors.userCompanyLabel.message : ""}
                      prefix={state.thirdparty.name}
                      {...field}
                    />
                  )}
                />
              </div>
            </div>
            <div className={classes.evaluateBtn}>
              <EAButton
                type="submit"
                disabled={!isEmpty(errors)}
                content={
                  <>
                    <EAColorIcon icon="newEnrichment" className={classes.evaluateBtnIcon} />
                    <span>
                      {i18next.t("securityRating.form.evaluate")}
                    </span>
                  </>
                }
                colorVariant="primaryLight"
                size="small"
                validateLoading={isLoading}
              />
            </div>
          </div>
          <div className={classes.assetsLimit}>
            <div className={classes.assetsLimitTitle}>
              {i18next.t("securityRating.form.analyzeLimit")}
              <EATooltip title={<AssetsTooltipTitle />} subTitle={<AssetsTooltipSubTitle />} placement="top" arrow variant="bi-color" width={500}>
                <span className="dt" >
                  <EAIcon icon={"info-rounded"} className={classes.infoIcon} />
                </span>
              </EATooltip>
            </div>
            <div className={classes.assetsLimitText}>
              <Trans i18nKey={`securityRating.form.assetsLimit`}
                components={{ underline: <u></u> }}
              />
            </div>
          </div>
        </form>
      </FormProvider >

      <SecurityRatingCompanyScopeModal
        isOpen={isCompanyScopeModalOpen}
        onClose={() => setCompanyScopeModalOpen(false)}
      />
    </div>
  )

}
export default SecurityRatingForm;
